import { Outlet } from "@tanstack/react-router";
import { useEffect, useRef } from "react";

import { usePlanStatus } from "@/react/hooks/usePlanStatus";

import { useUpgradePlanForm } from "./hooks/useUpgradePlanForm";
import { UpgradeFlowAnalyticsTracker } from "./routes/UpgradeFlowAnalyticsTracker";
import type { UpgradePlan } from "./types";
import {
  UpgradeFlowFormContextProvider,
  type UpgradeFlowFormContextValue,
} from "./UpgradeFlowContext";
import { useUpgradePlanContext } from "./UpgradePlanContext";

export const UpgradePlanRootLayout = () => {
  const {
    plan,
    usedForWorkflows: isUsedForWorkflows,
    showPlanDetails: shouldShowPlanDetails,
    source,
    conversationUuid,
    canCheckoutInstantly,
    isEmailHubOnlyUpgrade,
    shouldSkipProrationStep,
  } = useUpgradePlanContext();

  const { shouldShowProration } = usePlanStatus();

  const shouldShowProrationDetails = Boolean(
    shouldShowProration(plan) && !shouldSkipProrationStep,
  );

  const {
    onSubmitHandler,
    emailHubOnlySubmitHandler,
    changePlan: fullChangePlan,
    loadingMessage,
  } = useUpgradePlanForm({
    usedForWorkflows: isUsedForWorkflows || false,
    showProration: shouldShowProrationDetails,
    source: source || "",
    conversationUuid,
  });

  const handleChangePlan = (upgradePlan: UpgradePlan) => {
    fullChangePlan(upgradePlan);
  };

  useEffect(() => {
    if (canCheckoutInstantly) {
      onSubmitHandler();
    }
  }, [canCheckoutInstantly]);

  // canCheckoutInstantly requires a base plan, so it never fires for email-hub-only upgrades.
  // This effect fills that gap: when the modal opens without plan details, auto-submit immediately.
  const emailHubSubmittedRef = useRef(false);
  useEffect(() => {
    if (emailHubSubmittedRef.current) return;
    if (isEmailHubOnlyUpgrade && !shouldShowPlanDetails) {
      emailHubSubmittedRef.current = true;
      emailHubOnlySubmitHandler();
    }
  }, [emailHubOnlySubmitHandler, isEmailHubOnlyUpgrade, shouldShowPlanDetails]);

  const onSubmitHandlerPlanDetails = () => {
    if (isEmailHubOnlyUpgrade) {
      return emailHubOnlySubmitHandler();
    }
    return onSubmitHandler();
  };

  const formContextValue: UpgradeFlowFormContextValue = {
    onSubmitHandler,
    onSubmitHandlerPlanDetails,
    handleChangePlan,
    loadingMessage,
  };

  return (
    <UpgradeFlowFormContextProvider value={formContextValue}>
      <UpgradeFlowAnalyticsTracker />
      <Outlet />
    </UpgradeFlowFormContextProvider>
  );
};
